import { FieldLabel, Toggle } from "@alch/ui";
import clsx from "clsx";
import { Control, useController } from "react-hook-form";
import z from "zod";
import SimpleToggleGroup from "../simple-toggle-group/SimpleToggleGroup";
import TextField from "../text-field/TextField";

const notSupported = (
  <div className="text-label-100 text-critical-600">unsupported type</div>
);

const FlagInputControl = ({
  name,
  namePrefix,
  schema,
  control,
}: FlagInputProps) => {
  const controlName = [namePrefix, name].filter(Boolean).join(".");
  const { field } = useController({
    control,
    name: controlName,
  });

  const schemaDef = schema._def;

  switch (schemaDef.typeName) {
    case z.ZodFirstPartyTypeKind.ZodBoolean:
      return (
        <Toggle
          checked={Boolean(field.value)}
          onCheckedChange={field.onChange}
        />
      );
    case z.ZodFirstPartyTypeKind.ZodString:
      return (
        <TextField
          control={control}
          name={controlName}
          size="sm"
          placeholder="string"
        />
      );
    case z.ZodFirstPartyTypeKind.ZodObject:
      return (
        <div
          className={clsx(
            "mt-4 flex flex-col gap-5 first:mt-0",
            name ? "border-l-2 border-grayscale-200 pl-3" : "",
          )}
        >
          {Object.entries(
            schemaDef.shape() as Record<string, z.ZodFirstPartySchemaTypes>,
          ).map(([key, value]) => (
            <FlagInput
              key={key}
              name={key}
              schema={value}
              control={control}
              namePrefix={name}
            />
          ))}
        </div>
      );
    case z.ZodFirstPartyTypeKind.ZodUnion: {
      const options = schemaDef.options as z.ZodFirstPartySchemaTypes[];
      const allLiterals = options.every(
        (o: z.ZodFirstPartySchemaTypes): o is z.ZodLiteral<unknown> =>
          o._def.typeName === z.ZodFirstPartyTypeKind.ZodLiteral,
      );

      if (!allLiterals) return notSupported;

      return (
        <SimpleToggleGroup
          type="single"
          value={String(field.value)}
          onValueChange={field.onChange}
          items={options.map((option) => ({
            label: String(option._def.value),
            value: String(option._def.value),
          }))}
        />
      );
    }
    default:
      return notSupported;
  }
};

interface FlagInputProps {
  control: Control;
  name?: string;
  namePrefix?: string;
  schema: z.ZodFirstPartySchemaTypes;
}

const FlagInput = ({ name, namePrefix, control, schema }: FlagInputProps) => {
  return (
    <div>
      <FieldLabel label={name} />

      <FlagInputControl
        name={name}
        namePrefix={namePrefix}
        schema={schema}
        control={control}
      />
    </div>
  );
};

export default FlagInput;
